import { Injectable } from '@angular/core';
import {
  AiActionItemCounts,
  AiActionItemsBlock,
  AiActionItemsDataOutput,
  AiAssistantBlock,
  AiAssetItem,
  AiAssetRelationSummary,
  AiBackendAssetItem,
  AiBackendAssetRelation,
  AiBackendTaskItem,
  AiBackendToolOutput,
  AiBranchNarrationBlock,
  AiDoneStreamEvent,
  AiEvidenceSummary,
  AiNarrationSection,
  AiPromptAudit,
  AiTaskContextBlock,
  AiTaskContextDataOutput,
  AiTaskItem,
  AiTaskLineageBlock,
  AiTaskLineageDataOutput,
  AiToolResultStreamEvent,
  NvcSectionDef,
  TaskContextSectionBlock,
  TaskContextTotals,
  TaskLineageSectionBlock,
} from '../models/ai-assistant.models';
import { resolveAiDataRef } from '../utils/ai-data-ref-resolver';

@Injectable({ providedIn: 'root' })
export class AiAssistantResponseMapperService {
  mapToolResultToBlocks(event: AiToolResultStreamEvent): AiAssistantBlock[] {
    // These tools emit dedicated SSE events that produce their own blocks —
    // suppress the generic tool_result block.
    const toolName = event.output.tool || event.output.toolResult?.toolName || '';
    if (toolName === 'get_action_items' || toolName === 'get_task_context' || toolName === 'trace_task_lineage') {
      return [];
    }

    const visuals = event.output.visuals || [];

    if (!visuals.length) {
      return this.mapToolOutputToTaskBlock(event.output);
    }

    return visuals.flatMap((visual) => {
      if (visual.type === 'table' || visual.type === 'text') {
        const visualData = resolveAiDataRef(
          {
            toolResult: event.output.toolResult,
            output: event.output,
          },
          visual.dataRef,
        );

        return this.mapToolOutputToTaskBlock(event.output, visual.title, visualData, visual.id);
      }

      return [
        {
          id: visual.id,
          type: 'only-narration',
          narration: `${visual.title || 'Tool result'} is ready, but visual type "${visual.type}" is not mapped yet.`,
        },
      ];
    });
  }

  /**
   * Builds an AiTaskContextBlock from the done event.
   *
   * New path (get_task_context with task_context_data event):
   *   taskContextData carries the raw arrays; done.answer carries the LLM prose
   *   (already rendered in message.text — the block only shows tables).
   *
   * Legacy fallback: reads narrationPacket.sections from the first successful branch.
   */
  mapDoneToTaskContextBlock(
    event: AiDoneStreamEvent,
    taskContextData?: AiTaskContextDataOutput | null,
    hasSiblingLineageBlock = false,
  ): AiTaskContextBlock | null {
    const branches = event.branches || [];

    // ── Contract-driven path ─────────────────────────────────────────────────
    if (taskContextData) {
      const branch =
        branches.find((b) => b.toolName === 'get_task_context' && b.status === 'success') ??
        branches.find((b) => b.status === 'success');

      const answer = event.answer ?? '';
      // When trace_task_lineage renders alongside this card in the same multi-tool
      // response, its own "Linked assets" / "Action items" tables already show this
      // exact data (richer, with lineage context) — drop the duplicate large tables
      // here and keep only the prose-driven sections + the small risk/frameworks tables.
      const nvcSections = (taskContextData.uiSections ?? []).filter(
        (sec) => !hasSiblingLineageBlock || (sec.key !== 'assets' && sec.key !== 'action_items'),
      );
      const sectionBlocks = this.buildSectionBlocks(answer, nvcSections, taskContextData);

      return {
        id: `task-context-${branch?.branchId ?? Date.now()}`,
        type: 'task-context',
        taskId: branch?.toolName ?? 'get_task_context',
        narration: answer,
        sectionBlocks,
        linkedActionItems: taskContextData.linkedActionItems,
        linkedAssets: taskContextData.linkedAssets,
        linkedRisks: taskContextData.linkedRisks,
        totals: taskContextData.totals,
      };
    }

    // ── Legacy UNI path ─────────────────────────────────────────────────────
    const branch = branches.find(
      (b) => b.status === 'success' && b.narrationPacket?.sections?.length,
    );
    if (!branch) return null;

    const sections = (branch.narrationPacket!.sections ?? []) as AiNarrationSection[];
    if (!sections.length) return null;

    return {
      id: `task-context-${branch.branchId ?? Date.now()}`,
      type: 'task-context',
      taskId: branch.toolName ?? branch.branchId ?? 'task-context',
      sections,
    };
  }

  private buildSectionBlocks(
    answer: string,
    nvcSections: NvcSectionDef[],
    taskContextData: AiTaskContextDataOutput,
  ): TaskContextSectionBlock[] {
    // Multi-tool synthesized answers carry a backend-computed sectionProse map
    // (keyed by this tool's OWN uiSections key) — prefer it over fuzzy heading-text
    // matching, which only works when the answer's headings were written for this
    // tool's own llmHeading labels (true for single-tool answers, not combined ones).
    const proseMap = taskContextData.sectionProse ?? this.parseProseBySections(answer, nvcSections);
    const dataMap: Record<string, Record<string, unknown>[]> = {
      linkedActionItems: taskContextData.linkedActionItems as Record<string, unknown>[],
      linkedAssets: taskContextData.linkedAssets as Record<string, unknown>[],
      linkedRisks: taskContextData.linkedRisks as Record<string, unknown>[],
      linkedFrameworks: (taskContextData.linkedFrameworks ?? []) as Record<string, unknown>[],
    };
    const totals = taskContextData.totals;

    return nvcSections.map((sec): TaskContextSectionBlock => {
      const data = sec.dataSource ? (dataMap[sec.dataSource] ?? []) : [];
      const total = sec.dataSource ? this.resolveSectionTotal(sec.dataSource, totals, data) : 0;
      return {
        key: sec.key,
        title: sec.llmHeading,
        prose: proseMap[sec.key] ?? '',
        uiComponent: sec.uiComponent as TaskContextSectionBlock['uiComponent'],
        data: data.slice(0, 7),
        total,
      };
    });
  }

  private parseProseBySections(
    answer: string,
    sections: NvcSectionDef[],
  ): Record<string, string> {
    const result: Record<string, string> = {};
    const lines = answer.split('\n');
    let currentKey: string | null = null;
    const preHeading: string[] = [];
    let hitFirstHeading = false;
    const buffer: string[] = [];

    const flush = (): void => {
      if (currentKey !== null) {
        result[currentKey] = buffer.join('\n').trim();
        buffer.length = 0;
      }
    };

    for (const line of lines) {
      // Match ## or ### headings
      const headingMatch = line.match(/^#{2,3} (.+)$/);
      if (headingMatch) {
        flush();
        hitFirstHeading = true;
        const heading = headingMatch[1].trim().toLowerCase();
        const sec = sections.find((s) => {
          const lh = s.llmHeading.toLowerCase();
          return heading.includes(lh) || lh.includes(heading.split('(')[0].trim());
        });
        currentKey = sec?.key ?? null;
      } else if (hitFirstHeading && currentKey !== null) {
        buffer.push(line);
      } else if (!hitFirstHeading) {
        preHeading.push(line);
      }
    }
    flush();

    // If LLM used single heading (not per-section), assign pre-heading + any unparsed
    // content to the first prose section so narration is never silently dropped.
    const anyProse = Object.values(result).some((p) => p.length > 0);
    if (!anyProse) {
      const firstProseSection = sections.find((s) => !s.uiComponent);
      if (firstProseSection) {
        result[firstProseSection.key] = answer.trim();
      }
    } else {
      // Assign content captured before the first ## heading to first prose section
      const preText = preHeading.join('\n').trim();
      if (preText) {
        const firstProseSection = sections.find((s) => !s.uiComponent);
        if (firstProseSection && !result[firstProseSection.key]) {
          result[firstProseSection.key] = preText;
        }
      }
    }

    return result;
  }

  private resolveSectionTotal(
    dataSource: string,
    totals: TaskContextTotals,
    data: Record<string, unknown>[],
  ): number {
    // dataSource "linkedActionItems" → totals key "actionItems"
    const base = dataSource.startsWith('linked') ? dataSource.slice(6) : dataSource;
    const key = base.charAt(0).toLowerCase() + base.slice(1);
    return (totals as unknown as Record<string, number>)[key] ?? data.length;
  }

  /** One block per branch's own independently-produced narration (see AiBranchNarrationSection). */
  mapDoneToBranchNarrationBlocks(event: AiDoneStreamEvent): AiBranchNarrationBlock[] {
    return (event.sections || [])
      .filter((section) => !!section.text)
      .map((section) => ({
        id: `branch-narration-${section.branchId}`,
        type: 'branch-narration' as const,
        branchId: section.branchId,
        toolName: section.toolName ?? null,
        source: section.source,
        text: section.text,
      }));
  }

  mapDoneToActionItemsBlock(
    actionItemsData: AiActionItemsDataOutput,
  ): AiActionItemsBlock {
    const items = actionItemsData.items.slice(0, 8);
    const rawCounts = actionItemsData.counts ?? { total: 0, open: 0, verified: 0 };
    const trueTotal = rawCounts.total > 0 ? rawCounts.total : actionItemsData.items.length;
    const counts: AiActionItemCounts = {
      total: trueTotal,
      open: rawCounts.open > 0 ? rawCounts.open
        : items.filter(i => !i.status || i.status.toLowerCase() === 'to do' || i.status.toLowerCase() === 'open').length,
      verified: rawCounts.verified,
    };
    return {
      id: `action-items-${Date.now()}`,
      type: 'action-items',
      taskId: actionItemsData.taskId,
      task: actionItemsData.task ?? undefined,
      items,
      counts,
      flags: actionItemsData.flags,
    };
  }

  mapDoneToTaskLineageBlock(
    event: AiDoneStreamEvent,
    lineageData?: AiTaskLineageDataOutput | null,
  ): AiTaskLineageBlock | null {
    if (!lineageData) return null;
    const branches = event.branches || [];
    const branch =
      branches.find((b) => b.toolName === 'trace_task_lineage' && b.status === 'success') ??
      branches.find((b) => b.status === 'success');

    const answer = event.answer ?? '';
    const nvcSections = lineageData.uiSections ?? [];
    const sectionBlocks = this.buildLineageSectionBlocks(answer, nvcSections, lineageData);

    return {
      id: `task-lineage-${branch?.branchId ?? Date.now()}`,
      type: 'task-lineage',
      taskId: (lineageData.task?.['taskId'] as string | undefined) ?? (lineageData.task?.['id'] as string | undefined) ?? '',
      narration: answer,
      sectionBlocks,
      task: lineageData.task,
      linkedRisks: lineageData.linkedRisks,
      linkedAssets: lineageData.linkedAssets ?? [],
      assetSummary: lineageData.assetSummary,
      linkedActionItems: lineageData.linkedActionItems,
      lineageTree: lineageData.lineageTree,
      totals: lineageData.totals,
    };
  }

  private buildLineageSectionBlocks(
    answer: string,
    nvcSections: NvcSectionDef[],
    lineageData: AiTaskLineageDataOutput,
  ): TaskLineageSectionBlock[] {
    // See buildSectionBlocks — same multi-tool sectionProse preference.
    const proseMap = lineageData.sectionProse ?? this.parseProseBySections(answer, nvcSections);
    const dataMap: Record<string, Record<string, unknown>[]> = {
      linkedRisks: lineageData.linkedRisks as Record<string, unknown>[],
      linkedActionItems: lineageData.linkedActionItems as Record<string, unknown>[],
    };

    return nvcSections.map((sec): TaskLineageSectionBlock => {
      // assetSummary and lineageTree are objects — data array stays empty;
      // the component reads them directly from the block.
      const data = sec.dataSource && dataMap[sec.dataSource] ? dataMap[sec.dataSource].slice(0, 7) : [];
      const total = sec.dataSource && dataMap[sec.dataSource] ? dataMap[sec.dataSource].length : 0;
      return {
        key: sec.key,
        title: sec.llmHeading,
        prose: proseMap[sec.key] ?? '',
        uiComponent: sec.uiComponent as TaskLineageSectionBlock['uiComponent'],
        data,
        total,
      };
    });
  }

  createAudit(query: string, payload: Record<string, unknown>): AiPromptAudit {
    return {
      id: `audit-${Date.now()}`,
      systemPrompt: 'Backend-managed assistant prompt.',
      userPrompt: query,
      interpretedIntent: 'Backend stream response',
      tool: 'auto',
      payload,
    };
  }

  private mapToolOutputToTaskBlock(
    output: AiBackendToolOutput,
    title = 'Tool Result',
    visualData?: unknown,
    blockId = `tool-table-${Date.now()}`,
  ): AiAssistantBlock[] {
    const assetData = this.getAssetData(output, visualData);

    if ((assetData.assets || []).length) {
      return this.mapAssetDataToBlock(assetData, title, blockId);
    }

    const data = this.getTaskData(output, visualData);
    const tasks = (data.items || []).slice(0, 8).map((item) => this.mapTaskItem(item));
    const total = data.count ?? data.counts?.total ?? tasks.length;

    if (!tasks.length) {
      if (this.isTaskContextOutput(output, title)) {
        return [];
      }

      return [
        {
          id: `tool-empty-${Date.now()}`,
          type: 'only-narration',
          narration: `${title} returned no rows to display.`,
        },
      ];
    }

    return [
      {
        id: blockId,
        type: 'task-list',
        totalLabel: `${title} (${total})`,
        tasks,
      },
    ];
  }

  private getTaskData(
    output: AiBackendToolOutput,
    visualData?: unknown,
  ): { count?: number; counts?: { total?: number }; items?: AiBackendTaskItem[] } {
    if (this.hasTaskItems(visualData)) {
      return visualData;
    }

    return output.toolResult?.data || {};
  }

  private getAssetData(
    output: AiBackendToolOutput,
    visualData?: unknown,
  ): { count?: number; assets?: AiBackendAssetItem[] } {
    const visualAssetData = this.toAssetData(visualData);

    if (visualAssetData.assets?.length) {
      return visualAssetData;
    }

    return this.toAssetData(output.toolResult?.data);
  }

  private mapAssetDataToBlock(
    data: { count?: number; assets?: AiBackendAssetItem[] },
    title: string,
    blockId: string,
  ): AiAssistantBlock[] {
    const assets = (data.assets || []).slice(0, 8).map((asset) => this.mapAssetItem(asset));

    return [
      {
        id: blockId,
        type: 'asset-list',
        totalLabel: `${title} (${data.count ?? assets.length})`,
        assets,
      },
    ];
  }

  private isTaskContextOutput(output: AiBackendToolOutput, title: string): boolean {
    const values = [output.tool, output.toolResult?.toolName, title]
      .filter((value): value is string => typeof value === 'string')
      .map((value) => value.toLowerCase().replace(/[_-]+/g, ' '));

    return values.some((value) => value.includes('task context'));
  }

  private hasTaskItems(
    value: unknown,
  ): value is { count?: number; counts?: { total?: number }; items?: AiBackendTaskItem[] } {
    return (
      typeof value === 'object' &&
      value !== null &&
      Array.isArray((value as { items?: unknown }).items)
    );
  }

  private toAssetData(value: unknown): { count?: number; assets?: AiBackendAssetItem[] } {
    if (Array.isArray(value)) {
      return {
        count: value.length,
        assets: value as AiBackendAssetItem[],
      };
    }

    if (!value || typeof value !== 'object') {
      return {};
    }

    const record = value as {
      count?: number;
      total?: number;
      assets?: AiBackendAssetItem[];
      assetList?: AiBackendAssetItem[];
    };
    const assets = Array.isArray(record.assets)
      ? record.assets
      : Array.isArray(record.assetList)
        ? record.assetList
        : undefined;

    return {
      count: record.count ?? record.total ?? assets?.length,
      assets,
    };
  }

  private mapTaskItem(item: AiBackendTaskItem): AiTaskItem {
    const mappedStandards = this.extractMappedStandards(item);
    const assignedTo = typeof item.assigned === 'string'
      ? item.assigned
      : this.firstString(item.assigned?.name, item.assigned?.email);
    const assignedToAvatar = typeof item.assigned === 'object'
      ? this.firstString(item.assigned?.avatarUrl, item.assigned?.photo, item.assigned?.avatar, item.assigned?.profilePhoto)
      : undefined;

    return {
      taskId: item.actionItemId || item.taskId || item.foxID || 'Task',
      title: item.title || item.control || item.description || 'Untitled task',
      priority: item.priority || 'Unknown',
      risk: item.risk || item.impact || 'Unknown',
      status: item.status || 'Unknown',
      description: item.description,
      dueDate: item.dueDate,
      assignedTo,
      assignedToAvatar,
      progress: item.progress,
      domain: this.firstString(
        this.formatDomainMappings(item.domainSubdomainMappings)?.[0],
        item.domain,
        item.domainName,
        item.domainId,
      ),
      routeTaskId: item.taskId || item.foxID || item.actionItemId,
      routeActionItemId: item.actionItemId,
      assets: this.mergeOptionalRelationSummaries(
        'Assets',
        this.mapLooseRelationSummary('Assets', item.assetList, item.assetContext?.total),
        this.mapTaskRelationSummary(
          'Assets',
          item.associatedAssets,
          item.associatedAssetCount,
          'assetId',
          this.mergeIds(item.linkedAssetIds, item.assetIds),
          item.assetId,
        ),
      ),
      risks: this.mergeOptionalRelationSummaries(
        'Associated Risk',
        this.mapLooseRelationSummary('Associated Risk', item.riskList),
        this.mapTaskRelationSummary(
          'Associated Risk',
          item.associatedRisks,
          item.associatedRiskCount,
          'riskId',
          this.mergeIds(item.linkedRiskIds, item.riskIds),
          item.riskId,
        ),
      ),
      frameworks: this.mapLooseRelationSummary(
        'Framework',
        mappedStandards,
        this.countLooseRelations(mappedStandards),
      ),
      domains: this.mapTaskRelationSummary(
        'Domains',
        item.associatedDomains,
        item.associatedDomainCount,
        'domainId',
        this.mergeIds(this.formatDomainMappings(item.domainSubdomainMappings), item.domains),
        this.firstString(item.domain, item.domainName, item.domainId),
      ),
      actionItems: this.mapTaskRelationSummary(
        'Action Items',
        item.associatedActionItems,
        item.associatedActionItemCount,
        'actionItemId',
        item.linkedActionItemIds,
        item.actionItemId,
      ),
      evidence: this.mapEvidenceSummary(item),
    };
  }

  private mapEvidenceSummary(item: AiBackendTaskItem): AiEvidenceSummary | undefined {
    const ids = [
      ...(item.linkedEvidenceIds || []),
      ...(item.evidenceIds || []),
      ...(item.associatedEvidence || []).map((e) => e.evidenceId || e.id).filter((v): v is string => !!v),
    ].filter((v, i, a) => v && a.indexOf(v) === i);

    const total = item.associatedEvidenceCount ?? ids.length;
    if (!total) return undefined;

    const verificationResult = this.resolveEvidenceVerification(item);

    return { ids, total, verificationResult };
  }

  private resolveEvidenceVerification(item: AiBackendTaskItem): AiEvidenceSummary['verificationResult'] {
    const raw = item.evidenceVerificationResult ||
      item.associatedEvidence?.[0]?.verificationResult ||
      item.associatedEvidence?.[0]?.verificationStatus;

    if (!raw) return undefined;
    const val = raw.toLowerCase();
    if (val.includes('pass') || val.includes('verified')) return 'passed';
    if (val.includes('fail')) return 'failed';
    if (val.includes('partial')) return 'partial';
    return 'pending';
  }

  private mapAssetItem(asset: AiBackendAssetItem): AiAssetItem {
    const mappedStandards = this.extractMappedStandards(asset);

    return {
      assetId: asset.assetId || 'Asset',
      name: asset.name,
      assetType: asset.assetType || 'Unknown',
      assetCategory: asset.assetCategory || 'Unknown',
      criticality: asset.criticality || 'Unknown',
      exposure: asset.exposure || 'Unknown',
      status: asset.statusname || asset.status || 'Unknown',
      location: this.formatLocation(asset.location),
      owner: asset.owner,
      assignedTo: asset.assignedTo?.name || asset.assignedTo?.email,
      risks: this.mergeRelationSummaries(
        'Associated Risk',
        this.mapLooseRelationSummary('Associated Risk', asset.riskList),
        this.mapRelationSummary(
          'Associated Risk',
          asset.associatedRisks,
          asset.associatedRiskCount,
          'riskId',
        ),
      ),
      frameworks: this.mapLooseRelationSummary(
        'Framework',
        mappedStandards,
        this.countLooseRelations(mappedStandards),
      ),
      actionItems: this.mapRelationSummary(
        'Action Items',
        asset.associatedActionItems,
        asset.associatedActionItemCount,
        'actionItemId',
      ),
      tasks: this.mapRelationSummary(
        'Tasks',
        asset.associatedParentTasks,
        asset.associatedParentTaskCount,
        'taskId',
      ),
    };
  }

  private mergeRelationSummaries(
    label: string,
    primary: AiAssetRelationSummary | undefined,
    fallback: AiAssetRelationSummary,
  ): AiAssetRelationSummary {
    if (!primary?.total) {
      return fallback;
    }

    const ids = [...primary.ids, ...fallback.ids].filter((value, index, values) => {
      return values.findIndex((candidate) => candidate === value) === index;
    });

    return {
      label,
      ids,
      total: Math.max(primary.total, ids.length, fallback.total),
    };
  }

  private mapLooseRelationSummary(
    label: string,
    values: unknown[] | undefined,
    count = values?.length,
  ): AiAssetRelationSummary | undefined {
    const ids = (values || [])
      .map((value) => this.looseRelationLabel(value))
      .filter((value, index, allValues): value is string => {
        return (
          typeof value === 'string' &&
          value.trim().length > 0 &&
          allValues.findIndex((candidate) => candidate === value) === index
        );
      });

    const total = count ?? ids.length;

    if (!total) {
      return undefined;
    }

    return {
      label,
      ids,
      total,
    };
  }

  private countLooseRelations(values: unknown[] | undefined): number | undefined {
    return Array.isArray(values) ? values.length : undefined;
  }

  private extractMappedStandards(asset: AiBackendAssetItem): unknown[] | undefined {
    return this.toUnknownArray(
      asset.mappedStandards,
      asset.mappedStandard,
      asset.standards,
      asset.frameworks,
      asset.mappedFrameworks,
      asset.frameworkMappings,
    );
  }

  private mergeOptionalRelationSummaries(
    label: string,
    primary: AiAssetRelationSummary | undefined,
    fallback: AiAssetRelationSummary | undefined,
  ): AiAssetRelationSummary | undefined {
    if (!primary?.total) {
      return fallback;
    }

    if (!fallback?.total) {
      return primary;
    }

    return this.mergeRelationSummaries(label, primary, fallback);
  }

  private toUnknownArray(...values: unknown[]): unknown[] | undefined {
    const selected = values.find((value) => {
      return Array.isArray(value) || (typeof value === 'string' && value.trim().length > 0);
    });

    if (Array.isArray(selected)) {
      return selected;
    }

    if (typeof selected === 'string') {
      return [selected];
    }

    return undefined;
  }

  private looseRelationLabel(value: unknown): string | undefined {
    if (typeof value === 'string') {
      return value;
    }

    if (!value || typeof value !== 'object') {
      return undefined;
    }

    const record = value as Record<string, unknown>;
    const directLabel = this.firstString(
      record['standardName'],
      record['frameworkName'],
      record['standard'],
      record['standardShortName'],
      record['standardId'],
      record['standardID'],
      record['standardCode'],
      record['shortCode'],
      record['mappedStandard'],
      record['framework'],
      record['frameworkId'],
      record['frameworkID'],
      record['frameworkCode'],
      record['frameworkShortName'],
      record['controlFramework'],
      record['displayName'],
      record['name'],
      record['title'],
      record['label'],
      record['shortName'],
      record['code'],
      record['assetId'],
      record['riskId'],
      record['riskID'],
      record['id'],
      record['_id'],
    );

    if (directLabel) {
      return directLabel;
    }

    return this.firstString(
      this.looseRelationLabel(record['standard']),
      this.looseRelationLabel(record['framework']),
      this.looseRelationLabel(record['controlFramework']),
      this.looseRelationLabel(record['mappedStandard']),
    );
  }

  private mapRelationSummary(
    label: string,
    relations: AiBackendAssetRelation[] | undefined,
    count: number | undefined,
    idKey: keyof AiBackendAssetRelation,
  ): AiAssetRelationSummary {
    const ids = (relations || [])
      .map((relation) => relation[idKey] || relation.id)
      .filter((value): value is string => typeof value === 'string' && value.length > 0);

    return {
      label,
      ids,
      total: count ?? ids.length,
    };
  }

  private mapTaskRelationSummary(
    label: string,
    relations: AiBackendAssetRelation[] | undefined,
    count: number | undefined,
    idKey: keyof AiBackendAssetRelation,
    explicitIds?: string[],
    fallbackId?: string,
  ): AiAssetRelationSummary | undefined {
    const ids = [
      ...(explicitIds || []),
      ...(relations || []).map((relation) => this.relationLabel(relation, idKey)),
      fallbackId,
    ].filter((value, index, values): value is string => {
      return (
        typeof value === 'string' &&
        value.trim().length > 0 &&
        values.findIndex((candidate) => candidate === value) === index
      );
    });
    const total = count ?? ids.length;

    if (!total) {
      return undefined;
    }

    return {
      label,
      ids,
      total,
    };
  }

  private relationLabel(
    relation: AiBackendAssetRelation,
    idKey: keyof AiBackendAssetRelation,
  ): string | undefined {
    return this.firstString(
      relation[idKey],
      relation.id,
      relation.assetId,
      relation.riskId,
      relation.domainId,
      relation.domain,
      relation.domainName,
      relation.name,
      relation.title,
      relation.label,
    );
  }

  private mergeIds(...sources: (string[] | undefined)[]): string[] | undefined {
    const values = sources.flatMap((source) => source || []);
    return values.length ? values : undefined;
  }

  private formatDomainMappings(
    mappings: AiBackendTaskItem['domainSubdomainMappings'],
  ): string[] | undefined {
    const values = (mappings || [])
      .map((mapping) => {
        const domain = mapping.domain?.trim();
        const subdomain = mapping.subdomain?.trim();

        if (domain && subdomain) {
          return `${domain} / ${subdomain}`;
        }

        return domain || subdomain;
      })
      .filter((value, index, allValues): value is string => {
        return (
          typeof value === 'string' &&
          value.length > 0 &&
          allValues.findIndex((candidate) => candidate === value) === index
        );
      });

    return values.length ? values : undefined;
  }

  private firstString(...values: unknown[]): string | undefined {
    return values.find((value): value is string => {
      return typeof value === 'string' && value.trim().length > 0;
    });
  }

  private formatLocation(location: unknown): string | undefined {
    if (typeof location === 'string') {
      return location;
    }

    if (!location || typeof location !== 'object') {
      return undefined;
    }

    const record = location as Record<string, unknown>;
    const parts = [
      record['location'],
      record['name'],
      record['city'],
      record['bu'],
      record['region'],
      record['org'],
      record['country'],
    ].filter((value, index, values): value is string => {
      return (
        typeof value === 'string' &&
        value.trim().length > 0 &&
        values.findIndex((candidate) => candidate === value) === index
      );
    });

    if (parts.length) {
      return parts.join(', ');
    }

    const id = record['id'] || record['_id'] || record['locationId'];
    return typeof id === 'string' && id.trim().length > 0 ? id : undefined;
  }
}
